import type {
  MotionDurations,
  MotionEasings,
  MotionOnboarding,
  MotionSprings,
  MotionTransitions,
} from '@/lib/motion/presets.types'

/**
 * Timing/easing tokens extracted from the `@keyframes` curves in
 * `client/src/index.css` (sidebar expand/collapse/content-swap, dropdown
 * open/close, expand-from-point dialog, accordion). Motion migrations of
 * those surfaces should read from here rather than hardcoding
 * durations/easings, so the animation stays visually identical to the CSS
 * it replaces.
 */

export const easings: MotionEasings = {
  entrance: [0.16, 1, 0.3, 1],
  sidebar: [0.22, 1, 0.36, 1],
  exit: [0.42, 0, 1, 1],
  expandIn: [0.32, 0.72, 0, 1.2],
  expandOut: [0.4, 0, 0.7, 0.2],
  sidebarContentIn: [0.34, 1.56, 0.64, 1],
}

export const durations: MotionDurations = {
  fast: 0.12,
  normal: 0.18,
  panel: 0.24,
  sidebarExpand: 0.22,
  sidebarCollapse: 0.16,
  sidebarContentOut: 0.14,
  collapse: 0.2,
  expandDialogOpen: 0.25,
  expandDialogClose: 0.15,
}

export const transitions: MotionTransitions = {
  dropdown: {
    open: { duration: durations.normal, ease: easings.entrance },
    close: { duration: durations.fast, ease: easings.exit },
  },

  sidebar: {
    expand: { duration: durations.sidebarExpand, ease: easings.sidebar },
    collapse: { duration: durations.sidebarCollapse, ease: easings.exit },
  },

  sidebarContent: {
    in: { duration: durations.panel, ease: easings.sidebarContentIn },
    out: { duration: durations.sidebarContentOut, ease: easings.exit },
  },

  collapse: { duration: durations.collapse, ease: 'easeOut' },

  expandDialog: {
    open: { duration: durations.expandDialogOpen, ease: easings.expandIn },
    close: { duration: durations.expandDialogClose, ease: easings.expandOut },
  },

  stepSwap: { type: 'spring', stiffness: 400, damping: 34 },
}

/**
 * Spring tokens for the motion-polish animations. Chosen from the dev
 * motion-lab intensity picks; physics springs rather than the tweens above.
 * Use these instead of inline stiffness/damping so the builder's motion
 * feel stays consistent.
 */
export const springs: MotionSprings = {
  accordionRow: { type: 'spring', stiffness: 500, damping: 24 },

  menu: { type: 'spring', stiffness: 420, damping: 24 },
  menuItemStagger: 0.05,

  switchThumb: { type: 'spring', stiffness: 500, damping: 30 },

  tabIndicator: { type: 'spring', stiffness: 420, damping: 22 },

  publishPulse: { type: 'spring', stiffness: 500, damping: 12 },
}

/**
 * Onboarding-wizard choreography tokens. Flow-scoped: only
 * `components/onboarding/` (and the page shell) should read these. The scan
 * status lines are time-staged theater — the sequence gates on the real
 * generate-rules response, so `scanStatusStagger` paces the copy, not the
 * work.
 */
export const onboarding: MotionOnboarding = {
  productChoice: {
    chosen: { duration: 0.22, ease: easings.entrance },
    other: { duration: 0.22, ease: easings.exit },
    dismiss: { delay: 0.18, duration: 0.28, ease: easings.exit },
    dismissMs: 460,
  },

  productHandoff: { type: 'spring', stiffness: 320, damping: 30 },

  stepSlide: { type: 'spring', stiffness: 380, damping: 32 },
  stepStagger: 0.06,

  rail: { type: 'spring', stiffness: 420, damping: 28 },

  stage: { type: 'spring', stiffness: 300, damping: 26 },
  scanStatusStagger: 1.1,

  dealCard: { type: 'spring', stiffness: 340, damping: 24 },
  dealCardStagger: 0.12,
  dealCardSettle: {
    duration: 0.9,
    opacityTimes: [0, 0.2, 0.7, 1],
    scaleTimes: [0, 0.7, 1],
  },

  scanSweep: {
    duration: 2.2,
    ease: 'easeInOut',
    repeat: Number.POSITIVE_INFINITY,
  },

  launch: {
    rocket: { duration: 0.8, ease: easings.expandOut },
    spark: { duration: 0.7, ease: easings.entrance },
  },

  launchExit: { delay: 1.0, duration: 0.35, ease: easings.exit },

  avatarSwap: {
    keyframes: { opacity: [1, 0.72, 1], scale: [1, 1.008, 1] },
    beatMs: 600,
  },

  // Finish outro: the rocket beat plays, then a brand-colored circle expands
  // from the Finish button and the route swaps underneath the cover.
  outro: {
    cover: { delay: 0.55, duration: 0.7, ease: easings.exit },
    reveal: { duration: 0.55, ease: easings.exit },
    revealHoldMs: 350,
    caption: { duration: 0.3 },
    captionDelay: 0.5,
  },

  welcome: {
    cover: { duration: 0.48, ease: easings.entrance },
    reveal: { duration: 0.52, ease: easings.exit },
    revealHoldMs: 650,
    caption: { duration: 0.3, ease: easings.entrance },
  },
}
